import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Store,
  Sparkles,
  MousePointerClick,
  Percent,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const KPI_STATS = [
  {
    title: "Active offers",
    value: "24",
    hint: "+3 vs last week",
    icon: Sparkles,
    positive: true,
  },
  {
    title: "Impressions (7d)",
    value: "18.4k",
    hint: "+12% vs prior period",
    icon: MousePointerClick,
    positive: true,
  },
  {
    title: "Avg. discount",
    value: "14.2%",
    hint: "Across live campaigns",
    icon: Percent,
    positive: null,
  },
] as const;

const WEEKLY_TREND = [
  { day: "Mon", impressions: 2100 },
  { day: "Tue", impressions: 2380 },
  { day: "Wed", impressions: 1950 },
  { day: "Thu", impressions: 2620 },
  { day: "Fri", impressions: 2890 },
  { day: "Sat", impressions: 3200 },
  { day: "Sun", impressions: 2750 },
];

const CHANNEL_MIX = [
  { channel: "Email", share: 42 },
  { channel: "In-app", share: 28 },
  { channel: "SMS", share: 18 },
  { channel: "Web", share: 12 },
];

const CHART_TOOLTIP_STYLE = {
  contentStyle: {
    borderRadius: "8px",
    border: "1px solid rgb(254 205 211)",
    fontSize: "12px",
  },
};

export default function OffersDashboard() {
  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-medium uppercase tracking-wide text-rose-600">
            Offer Point
          </p>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-gray-600 max-w-2xl">
            Plan and manage promotional offers, loyalty programs, and merchant
            partners from one workspace.
          </p>
        </div>
        <div />
      </div>

      {/* KPI card (full width) */}
      <Card className="border-rose-100 shadow-sm bg-gradient-to-br from-white to-rose-50/30">
        <CardContent className="pt-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {KPI_STATS.map((k) => (
              <div key={k.title} className="rounded-lg border border-rose-100 p-4">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {k.title}
                  </CardTitle>
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-100 text-rose-700">
                    <k.icon className="h-4 w-4" />
                  </div>
                </div>
                <div className="mt-3 text-2xl font-bold tracking-tight text-gray-900">
                  {k.value}
                </div>
                <p
                  className={`text-xs mt-1 ${
                    k.positive === true
                      ? "text-emerald-600"
                      : k.positive === false
                        ? "text-red-600"
                        : "text-muted-foreground"
                  }`}
                >
                  {k.hint}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="border-rose-100 shadow-sm lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-lg">Impressions</CardTitle>
            <CardDescription>Last 7 days</CardDescription>
          </CardHeader>
          <CardContent className="pl-0">
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={WEEKLY_TREND}
                  margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="fillImp" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="#e11d48"
                        stopOpacity={0.35}
                      />
                      <stop offset="100%" stopColor="#e11d48" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-rose-100"
                  />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={44}
                  />
                  <Tooltip {...CHART_TOOLTIP_STYLE} />
                  <Area
                    type="monotone"
                    dataKey="impressions"
                    name="Impressions"
                    stroke="#e11d48"
                    strokeWidth={2}
                    fill="url(#fillImp)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-rose-100 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Traffic by channel</CardTitle>
            <CardDescription>Share of offer views</CardDescription>
          </CardHeader>
          <CardContent className="pl-0">
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={CHANNEL_MIX}
                  layout="vertical"
                  margin={{ top: 8, right: 12, left: 4, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={false}
                    className="stroke-rose-100"
                  />
                  <XAxis
                    type="number"
                    domain={[0, 50]}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="channel"
                    width={56}
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip {...CHART_TOOLTIP_STYLE} />
                  <Bar
                    dataKey="share"
                    name="Share %"
                    fill="#fb7185"
                    radius={[0, 4, 4, 0]}
                    barSize={22}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick links */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="border-rose-100 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-800">
              <Sparkles className="h-5 w-5" />
            </div>
            <CardTitle className="text-lg">Programs</CardTitle>
            <CardDescription>
              Browse the list, create new programs, and edit program details.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              asChild
              variant="secondary"
              className="w-full bg-rose-600 text-white hover:bg-rose-700"
            >
              <Link to="/offers/programs">Open programs</Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="border-rose-100 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-100 text-rose-800">
              <Store className="h-5 w-5" />
            </div>
            <CardTitle className="text-lg">Merchants</CardTitle>
            <CardDescription>
              Partner merchants, contacts, and store locations in one place.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              asChild
              variant="outline"
              className="w-full border-rose-200 text-rose-900 hover:bg-rose-50"
            >
              <Link to="/offers/merchants">Open merchants</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
